<script>
  import { _, user } from "../../stores/index";
  import { onMount } from "svelte";
  import { AppsRepository } from "../../repositories/index";
  import { parseUrl, setUrl, setModalParams, clearModalParams } from "../../utils/index";
  import { ConfirmModal, AppModal, Layout } from "../../components/index";
  import { ModalType } from "../../types/modal";
  import { Link, navigate } from "svelte-routing";
  import {
    faPlus,
    faPen,
    faTrashAlt,
  } from "@fortawesome/pro-regular-svg-icons";
  import {
    SimpleDataTable,
    DataCell,
    Flex,
    FlexItem,
    Button,
    Icon,
    Text,
    Loader,
    DataTableExpansion,
    DataTableExpansionContent,
    Select,
    SelectItem,
    TextField,
    Page,
    PageHeader,
  } from "@gl/design-system";
  import {
    faSort,
    faSortDown,
    faSortUp,
  } from "@fortawesome/pro-solid-svg-icons";
  import { format } from "date-fns";

  let apps = [];
  let loading = {};
  let modal;
  let modalType;

  let confirmModal;
  let selectedApp;

  let search = { type: "all", name: "" };
  let availableTypes = [
    "gl-access-manager",
    "gl-compositor",
    "gl-graphics-manager",
    "gl-channel-manager",
    "gl-content-manager",
  ];
  let searched;

  let order = {
    asc: true,
  };

  const handleDelete = async () => {
    try {
      loading.delete = true;
      await AppsRepository.deleteApplication(selectedApp.id);
    } finally {
      fetchApps();
      loading.delete = true;
    }
  };

  const fetchApps = async () => {
    try {
      loading.apps = true;
      const result = await AppsRepository.getApplications();
      apps = result.list;

      const appsContracts = await Promise.all(
        apps.map((app) => AppsRepository.getContracts(app.id)),
      );
      apps = apps.map((app, index) => ({
        ...app,
        contracts: appsContracts[index].list.length,
      }));

      searched = apps;
    } finally {
      loading.apps = false;
    }
  };

  const handleSearch = () => {
    searched = apps
      .filter((app) => app.type === search.type || search.type === "all")
      .filter((app) =>
        app.name.toUpperCase().includes(search.name.trim().toUpperCase()),
      );
    if (order.by) {
      sort(order.by, false);
    } else {
      setUrl({
        ...search,
        orderBy: order.by,
        orderAsc: order.asc,
        appModal: modalType,
        appId: selectedApp?.id,
      });
    }
  };

  const handleReset = () => {
    search = { type: "all", name: "" };
    order = { asc: true };
    handleSearch();
  };

  function openModal(type, app = null) {
    modal = true;
    modalType = type;
    selectedApp = app;
    setModalParams("appModal", "appId", { type, id: app?.id });
  }

  function closeModal() {
    modal = false;
    selectedApp = null;
    modalType = null;
    clearModalParams("appModal", "appId");
  }

  function sort(field = null, toggleAsc = true) {
    if (toggleAsc) {
      order.asc = !order.asc;
    }
    order.by = field;

    const cmpA = order.asc ? -1 : 1;
    const cmpB = order.asc ? 1 : -1;
    searched = searched.sort((a, b) => {
      const A =
        field === "modified"
          ? a.metadata.modifiedAt
          : a[order.by].toUpperCase();
      const B =
        field === "modified"
          ? b.metadata.modifiedAt
          : b[order.by].toUpperCase();
      return A === B ? 0 : A < B ? cmpA : cmpB;
    });
    order.icon = order.asc ? "ArrowDown" : "ArrowUp";
    setUrl({
      ...search,
      orderBy: order.by,
      orderAsc: order.asc,
      appModal: modalType,
      appId: selectedApp?.id,
    });
  }

  onMount(async () => {
    if (!$user.data.isMaster) {
      navigate("/forbidden");
      return;
    }

    await fetchApps();

    const query = parseUrl(window.location.search);
    search = { ...search, type: query.type || "all", name: query.name || "" };
    if (query.orderBy) {
      order.by = query.orderBy;
      order.asc = query.orderAsc === "true";
    }

    if (query.appModal) {
      if (query.appId) selectedApp = apps.find((a) => a.id === query.appId);
      openModal(query.appModal, selectedApp);
    }

    handleSearch();
  });
</script>

{#if modal}
  <AppModal
    bind:active={modal}
    on:close={closeModal}
    {modalType}
    data={selectedApp}
    on:success={() => {
      fetchApps();
      closeModal();
    }}
  />
{/if}

{#if confirmModal}
  <ConfirmModal
    bind:active={confirmModal}
    on:close={() => {
      confirmModal = false;
      selectedApp = null;
    }}
    handleYes={handleDelete}
    title={$_("delete.resource", {
      values: { resourceType: $_("common.app") },
    })}
    confirmationValue={selectedApp.id}
  >
    <p>
      {@html $_("delete.warning.access2", {
        values: {
          resourceName: `${selectedApp?.name}`,
        },
      })}
    </p>

    <p class="mt-4 mb-2">
      {@html $_("delete.confirmation", {
        values: {
          resourceType: $_("common.app").toLowerCase(),
          resourceId: selectedApp?.id,
        },
      })}
    </p>
  </ConfirmModal>
{/if}

<Layout selected="globalApps">
  {#if loading.apps}
    <Loader />
  {:else}
    <Page
      title={`${$_("admin.title")} | ${$_("common.apps")} | ${$_("access-manager")}`}
    >
      <svelte:fragment slot="header">
        <PageHeader
          heading={$_("common.apps")}
          subtitle={$_("apps.total", {
            values: { total: apps.length },
          })}
        >
          <Button slot="actions" on:click={() => openModal(ModalType.Post)}>
            <Icon icon={faPlus} />
            <Text>{$_("apps.add_app")}</Text>
          </Button>
        </PageHeader>

        <Flex class="mb-5 align-items-center">
          <FlexItem class="mr-4">
            <Select bind:value={search.type} id="group">
              <SelectItem
                label={`${$_("common.type")}: ${$_("common.all")}`}
                value="all"
              />
              {#each availableTypes as type}
                <SelectItem
                  label={`${$_("common.type")}: ${type}`}
                  value={type}
                />
              {/each}
            </Select>
          </FlexItem>

          <FlexItem style="width:300px" class="mr-4">
            <TextField
              variant="Search"
              bind:value={search.name}
              on:keydown={(e) => {
                if (e.key === "Enter") {
                  handleSearch();
                }
              }}
            />
          </FlexItem>

          <FlexItem class="mr-4">
            <Button on:click={handleSearch}>
              {$_("common.search")}
            </Button>
          </FlexItem>

          <FlexItem>
            <Button outlined on:click={handleReset}>
              {$_("common.reset")}
            </Button>
          </FlexItem>
        </Flex>
      </svelte:fragment>

      <SimpleDataTable
        let:index
        let:row
        data={searched || apps}
        fullWidth
        selectable={false}
        loading={loading.apps}
        variant="separate"
        hasExpansions
        stickyHead
      >
        <slot name="empty" slot="empty">
          <div class="gl-message" style="text-align: center">
            {$_("common.no.display")}
          </div>
        </slot>
        <DataCell
          {index}
          id={`${row.id}-id`}
          header="ID"
          headerIcon={order.by === "id" && order.asc
            ? faSortUp
            : order.by === "id" && !order.asc
              ? faSortDown
              : faSort}
          clickableHeader
          activeHeader
          on:clickHeader={() => sort("id")}
          maxWidth={300}
        >
          <Link to={`access-manager/admin/apps/${row.id}/contracts`}
            >{row.id}</Link
          >
        </DataCell>

        <DataCell
          {index}
          header={$_("common.name")}
          id={`${row.id}-name`}
          maxWidth={300}
        >
          {row.name}
        </DataCell>

        <DataCell
          {index}
          header={$_("common.type")}
          id={`${row.id}-type`}
          maxWidth={300}
        >
          {row.type ?? "-"}
        </DataCell>

        <DataCell
          {index}
          header={$_("apps.policy.definition")}
          id={`${row.id}-policy`}
        >
          {row.policyDefinition}
        </DataCell>

        <DataCell
          {index}
          header={$_("admin.apps.contracts")}
          id={`${row.id}-policy`}
        >
          {row.contracts}
        </DataCell>

        <DataCell
          {index}
          id={`${row.id}-modified`}
          header={$_("common.last.modified")}
          headerIcon={order.by === "modified" && order.asc
            ? faSortUp
            : order.by === "modified" && !order.asc
              ? faSortDown
              : faSort}
          clickableHeader
          activeHeader
          on:clickHeader={() => sort("modified")}
        >
          {row.metadata?.modifiedAt
            ? format(new Date(row.metadata.modifiedAt), "dd.MM.yyyy HH:mm:ss")
            : "-"}
          {#if row.metadata?.modifiedBy}
            <p class="text-light" style="font-size:14px">
              {$_("common.by")}
              {row.metadata.modifiedBy}
            </p>
          {/if}
        </DataCell>

        <DataTableExpansion slot="expansion" let:row>
          {#if row.isExpanded}
            <DataTableExpansionContent colspan={7}>
              <Flex class="justify-content-end">
                <FlexItem>
                  <Button
                    outlined
                    class="mr-1  mb-1"
                    size="tiny"
                    on:click={() => openModal(ModalType.Put, row)}
                  >
                    <Icon icon={faPen} />
                    <Text>{$_("buttons.data")}</Text>
                  </Button>

                  <Button
                    outlined
                    class="mr-1 mb-1"
                    size="tiny"
                    on:click={() => {
                      navigate(`apps/${row.id}/contracts`);
                    }}
                  >
                    <Icon icon={faPen} />
                    <Text>{$_("common.contracts")}</Text>
                  </Button>

                  <Button
                    outlined
                    class="mr-1 mb-1"
                    size="tiny"
                    on:click={() => {
                      navigate(`apps/${row.id}/policies`);
                    }}
                  >
                    <Icon icon={faPen} />
                    <Text>{$_("common.policies")}</Text>
                  </Button>

                  <Button
                    outlined
                    size="tiny"
                    status="error"
                    on:click={() => {
                      selectedApp = row;
                      confirmModal = true;
                    }}
                  >
                    <Icon icon={faTrashAlt} />
                    <Text>{$_("buttons.delete")}</Text>
                  </Button>
                </FlexItem>
              </Flex>
            </DataTableExpansionContent>
          {/if}
        </DataTableExpansion>
      </SimpleDataTable>
    </Page>
  {/if}
</Layout>
